import cx from 'classnames';
import { FC, useMemo, useState } from 'react';

import { useAgencyConstants } from '@core/contexts/ConfigContext';
import { useOptions } from '@core/contexts/OptionsContext';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { DatePicker, MaterialDate } from '@shared/components/date-picker';
import { Flex } from '@shared/components/flex';
import { MultiGroupSelect } from '@shared/components/group-select-new/multi-group-select';
import { SingleGroupSelect } from '@shared/components/group-select-new/single-group-select';
import { NumberField } from '@shared/components/number-field';
import { Value } from '@shared/components/select/Select.types';
import { SingleSelect } from '@shared/components/select-new/single-select';
import { TextField } from '@shared/components/text-field';
import { UserAutocomplete } from '@shared/components/user-autocomplete';
import { useCoreOptions } from '@shared/contexts/OptionsContext';
import { ButtonSize } from '@shared/types/common/button';
import { LookupUserItem } from '@shared/types/common/common';
import { InputSize } from '@shared/types/common/input';
import { stringObjectToOptions } from '@shared/utils/options';

import { styles } from './SaleDetailsForm.styles';

export enum SaleType {
  Freehold = '1',
  VirtualFreehold = '2',
  Feuhold = '3',
  LongLeasehold = '4',
  Leasehold = '5',
}

export interface SaleDetailsFormData {
  assignee: LookupUserItem | undefined;
  capital: string;
  capitalCurrency: string;
  date: MaterialDate;
  investment: NumberBoolean;
  leaseComment: string;
  leaseExpiryMonth: string;
  leaseExpiryYear: string;
  netInitialYield: string;
  ourAcquisition?: boolean;
  ourDisposal?: boolean;
  passingRent: string;
  passingRentCurrency: string;
  passingRentMetric: string;
  purchasePrice: string;
  purchasePriceCurrency: string;
  saleType: string;
  sectors: string[];
  source: string;
  waultToBreak: string;
  waultToExpiry: string;
}

export interface SaleDetailsFormProps extends WithStyles<typeof styles> {
  data: SaleDetailsFormData;
  errors?: Record<string, boolean>;
  notInSchedule: boolean;
  updateData: (data: Partial<SaleDetailsFormData>) => void;
}

const SaleDetailsFormComponent: FC<SaleDetailsFormProps> = ({
  classes,
  data,
  errors,
  notInSchedule,
  updateData,
}) => {
  const { constants } = useAgencyConstants();
  const { currencyOptions } = useOptions();
  const { groupedPreferredSectorOptions, groupedPreferredSourceOptions, rentMetricsOptions } = useCoreOptions();

  const [changeAssignee, setChangeAssignee] = useState<boolean>(false);

  const monthOptions = useMemo(() => stringObjectToOptions(constants.regularMonths), [constants]);
  const saleTypeOptions = useMemo(() => stringObjectToOptions(constants.availableSaleTypeOptions), [constants]);

  return (
    <div className={classes.root}>
      <Flex justifyContent="space-between" className={classes.cardHeader}>
        <span className={classes.cardTitle}>{t('headline_details')}</span>
      </Flex>
      <div className={classes.twoColumnGrid}>
        <DatePicker
          label={t('completion_date')}
          dateFormat="DD/MM/YYYY"
          error={!!errors?.date}
          placeholder="DD/MM/YYYY"
          classes={{ picker: classes.datePicker, label: classes.label }}
          value={data.date}
          size={InputSize.medium}
          onChange={(date: MaterialDate) => updateData({ date: date })}
        />
        <SingleSelect
          classes={{ root: classes.rootSelectTenure, select: classes.select, labelRoot: classes.label }}
          label={t('sale_tenure')}
          error={!!errors?.saleType}
          placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
          value={data.saleType}
          options={saleTypeOptions}
          size={ButtonSize.medium}
          onChange={(value: Value) => updateData({ saleType: String(value) })}
        />
        {constants.localisation.currencyDropdownVisible && (
          <SingleSelect
            classes={{ root: classes.rootSelectWithBottomMargin, labelRoot: classes.label, select: classes.select }}
            label={t('purchase_price_currency_1')}
            placeholder={t('select')}
            fullWidth
            value={data.purchasePriceCurrency}
            size={ButtonSize.medium}
            options={currencyOptions}
            onChange={(value: Value) => updateData({ purchasePriceCurrency: value.toString() })}
          />
        )}
        <NumberField
          InputProps={{
            startAdornment: (
              <span className={classes.currencyAdornment}>
                {constants.localisation.currencyDefinitions[data.purchasePriceCurrency]?.symbol}
              </span>
            ),
          }}
          classes={{ labelError: classes.label, labelRoot: classes.label }}
          label={t('purchase_price')}
          fullWidth
          error={!!errors?.purchasePrice}
          value={data.purchasePrice}
          size={InputSize.medium}
          placeholder={t('enter_price')}
          onChange={(value: string) => updateData({ purchasePrice: value })}
          min={0}
        />
        {constants.localisation.currencyDropdownVisible && (
          <SingleSelect
            classes={{ select: classes.select, root: classes.rootSelect, labelRoot: classes.label }}
            label={t('capital_value_currency_1')}
            placeholder={t('select')}
            fullWidth
            value={data.capitalCurrency}
            size={ButtonSize.medium}
            options={currencyOptions}
            onChange={(value: Value) => updateData({ capitalCurrency: value.toString() })}
          />
        )}
        <NumberField
          classes={{ root: classes.numberField, labelRoot: classes.label }}
          InputProps={{
            startAdornment: (
              <span className={classes.currencyAdornment}>
                {constants.localisation.currencyDefinitions[data.capitalCurrency]?.symbol}
              </span>
            ),
          }}
          fullWidth
          size={InputSize.medium}
          label={t('capital_value_bracket_per_sq_ft_bracket')}
          placeholder={t('enter_capital_value')}
          value={data.capital}
          onChange={(value: string) => updateData({ capital: value })}
          min={0}
        />
      </div>

      {/* Investment Sale Fields */}
      {data.investment === 1 && (
        <div className={classes.twoColumnGrid}>
          {constants.localisation.currencyDropdownVisible && (
            <SingleSelect
              classes={{ select: classes.select, root: classes.rootSelect, labelRoot: classes.label }}
              label={t('passing_rent_currency_1')}
              placeholder={t('select')}
              fullWidth
              value={data.passingRentCurrency || data.purchasePriceCurrency}
              size={ButtonSize.medium}
              options={currencyOptions}
              onChange={(value: Value) => updateData({ passingRentCurrency: value.toString() })}
            />
          )}
          <div className={cx(classes.twoColumnGrid, classes.removeBottomMargin)}>
            <NumberField
              classes={{ root: classes.numberField, labelRoot: classes.label }}
              InputProps={{
                startAdornment: (
                  <span className={classes.currencyAdornment}>
                    {
                      constants.localisation.currencyDefinitions[data.passingRentCurrency || data.purchasePriceCurrency]
                        ?.symbol
                    }
                  </span>
                ),
              }}
              fullWidth
              label={t('tenancy_passing_rent_1')}
              placeholder={t('enter_rent')}
              size={InputSize.medium}
              value={data.passingRent || ''}
              onChange={(value: string) => updateData({ passingRent: value })}
              min={0}
            />
            <SingleSelect
              classes={{ select: classes.select, labelRoot: classes.label }}
              label={t('unit_of_measure_1')}
              placeholder={t('select')}
              value={data.passingRentMetric || ''}
              options={rentMetricsOptions}
              size={ButtonSize.medium}
              onChange={(value: Value) => updateData({ passingRentMetric: String(value) })}
            />
          </div>
          <NumberField
            classes={{ root: classes.numberField, labelRoot: classes.label }}
            InputProps={{
              endAdornment: <span className={classes.currencyAdornment}>%</span>,
            }}
            fullWidth
            label={t('net_initial_yield')}
            placeholder={t('enter_net_initial_yield')}
            size={InputSize.medium}
            value={data.netInitialYield || ''}
            onChange={(value: string) => updateData({ netInitialYield: value })}
            min={0}
          />
          <NumberField
            classes={{ root: classes.numberField, labelRoot: classes.label }}
            InputProps={{
              endAdornment: <span className={classes.currencyAdornment}>{t('years')}</span>,
            }}
            fullWidth
            label={t('wault_to_break_1')}
            placeholder={t('enter_wault_to_break')}
            size={InputSize.medium}
            value={data.waultToBreak || ''}
            onChange={(value: string) => updateData({ waultToBreak: value })}
            min={0}
          />
          <NumberField
            classes={{ root: classes.numberField, labelRoot: classes.label }}
            InputProps={{
              endAdornment: <span className={classes.currencyAdornment}>{t('years')}</span>,
            }}
            fullWidth
            label={t('wault_to_expiry_1')}
            placeholder={t('enter_wault_to_expiry')}
            size={InputSize.medium}
            value={data.waultToExpiry || ''}
            onChange={(value: string) => updateData({ waultToExpiry: value })}
            min={0}
          />
        </div>
      )}

      {/* Only show lease info for Long Leasehold and Leasehold */}
      {(data.saleType === SaleType.LongLeasehold || data.saleType === SaleType.Leasehold) && (
        <div className={classes.twoColumnGrid}>
          <NumberField
            classes={{ root: classes.numberField, labelRoot: classes.label }}
            label={t('lease_expiry_year')}
            fullWidth
            size={InputSize.medium}
            className={classes.descriptionInput}
            placeholder={t('xtextx_ellipsis', { text: t('year_lease_expires') })}
            onChange={(value: string) => updateData({ leaseExpiryYear: value })}
            value={data.leaseExpiryYear}
            min={0}
          />
          <SingleSelect
            classes={{ select: classes.select, root: classes.rootSelect, labelRoot: classes.label }}
            fullWidth
            label={t('lease_expiry_month')}
            placeholder={t('month_the_lease_expires')}
            value={data.leaseExpiryMonth}
            size={ButtonSize.medium}
            options={monthOptions}
            onChange={(value: Value) => updateData({ leaseExpiryMonth: String(value) })}
          />
          <Flex autoWidth={false} className={classes.fullWidth}>
            <TextField
              label={t('lease_comment_1')}
              fullWidth
              inputProps={{ maxLength: 1000 }}
              size={InputSize.medium}
              className={classes.descriptionInput}
              placeholder={t('xtextx_ellipsis', { text: t('comment_regarding_the_lease') })}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateData({ leaseComment: e.target.value })}
              value={data.leaseComment}
            />
          </Flex>
        </div>
      )}
      <div className={classes.divider} />
      <Flex className={classes.itemsLine} justifyContent="flex-start" wrap="nowrap">
        {notInSchedule && (
          <MultiGroupSelect
            classes={{ labelRoot: classes.label, tooltip: classes.notInScheduleSelect }}
            fullWidth
            groupOptions={groupedPreferredSectorOptions}
            label={t('sectors')}
            placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
            size={ButtonSize.medium}
            value={data.sectors}
            onChange={(sectors) => updateData({ sectors: sectors as string[] })}
          />
        )}
        <SingleGroupSelect
          classes={{ labelRoot: classes.label, root: classes.notInScheduleSelect }}
          fullWidth
          groupOptions={groupedPreferredSourceOptions}
          label={t('source')}
          placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
          size={ButtonSize.medium}
          value={data.source}
          onChange={(source) => updateData({ source: source as string })}
        />
      </Flex>
      <div className={classes.divider} />
      {!changeAssignee && data.assignee ? (
        <Flex direction="row">
          <div className={classes.defaultText}>
            {t('transaction_will_be_assigned_to_xnamex', { name: data.assignee.name })}{' '}
          </div>
          <div
            className={classes.changeAssigneeBtn}
            onClick={() => {
              updateData({ assignee: undefined });
              setChangeAssignee(true);
            }}
          >
            [ {t('change_assignee')} ]
          </div>
        </Flex>
      ) : (
        <UserAutocomplete
          dropUp={false}
          label={t('assignee')}
          setUser={(user) => updateData({ assignee: user })}
          classes={{ root: classes.userAutocomplete, label: classes.label }}
        />
      )}
    </div>
  );
};

export const SaleDetailsForm = withStyles(styles)(SaleDetailsFormComponent);
